import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchableDropdown from '../components/SearchableDropdown';
import CountryPhoneInput from '../components/CountryPhoneInput';
import axios from 'axios';
import { trackFeatureUsage, trackSearch, trackFormSubmission, trackEvent } from '../utils/analytics';
import { calculateAgeFromDateString, formatDateForDisplay } from '../utils/dateFormat';
import { calculateIdealWeight } from '../utils/healthCalculations';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const Customers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('fullName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const statusFilterRef = React.useRef(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    gender: 'MALE',
    phone: '',
    phoneCountryId: null,
    address: '',
    pcId: '',
    customerType: 'ONLINE',
    referredById: '',
    referredBySearch: '',
    showReferralDropdown: false,
    filteredReferralOptions: [],
    checkupDay: 'MONDAY',
    program: '',
    memberRoleCode: 'CUSTOMER',
    programDiscountSlabId: '',
    productDiscountSlabId: '',
    dateOfBirth: '',
    age: '',
    heightCm: '',
    idealWeightKg: '',
    initialWeightKg: '',
    discomfortsMedications: ''
  });
  const [immediateChildren, setImmediateChildren] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [discountPlans, setDiscountPlans] = useState([]);
  const [productDiscountPlans, setProductDiscountPlans] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [emailValidation, setEmailValidation] = useState({
    isChecking: false,
    isValid: true,
    message: ''
  });
  const [usernameValidation, setUsernameValidation] = useState({
    isChecking: false,
    isValid: true,
    message: ''
  });


  useEffect(() => {
    // Check for status filter in URL parameters on component mount
    const urlParams = new URLSearchParams(location.search);
    const statusParam = urlParams.get('status');
    if (statusParam && ['ACTIVE', 'INACTIVE', 'ARCHIVED'].includes(statusParam)) {
      setStatusFilter([statusParam]);
    } else {
      // Set default to ACTIVE only if no URL parameter is present
      setStatusFilter(['ACTIVE']);
    }
    setIsInitialized(true);
  }, [location.search]);

  useEffect(() => {
    // Only fetch data after initialization is complete
    if (!isInitialized) return;
    
    fetchCustomers();
    fetchImmediateChildren();
    fetchDiscountPlans();
    fetchProductDiscountPlans();
    
    // Track customers page view on initial load
    if (currentPage === 0 && !searchQuery) {
      trackFeatureUsage('Customers Management', 'view');
    }
  }, [isInitialized, currentPage, sortField, sortDirection, searchQuery, statusFilter]);

  // Set default MRP discount plan when discount plans are loaded
  useEffect(() => {
    if (discountPlans.length > 0 && !newCustomer.programDiscountSlabId) {
      const mrpPlan = discountPlans.find(plan => 
        plan.planName.toLowerCase().includes('mrp')
      );
      if (mrpPlan) {
        setNewCustomer(prev => ({...prev, programDiscountSlabId: mrpPlan.id}));
      }
    }
  }, [discountPlans, newCustomer.programDiscountSlabId]);

  useEffect(() => {
    if (productDiscountPlans.length > 0 && !newCustomer.productDiscountSlabId) {
      const mrpPlan = productDiscountPlans.find(plan =>
        (plan.planCode || '').toUpperCase() === 'MRP' ||
        (plan.planName || '').toLowerCase().includes('mrp')
      );
      if (mrpPlan) {
        setNewCustomer(prev => ({ ...prev, productDiscountSlabId: mrpPlan.id }));
      }
    }
  }, [productDiscountPlans, newCustomer.productDiscountSlabId]);

  // Close status filter when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Update program options based on customer type
    if (newCustomer.customerType === 'ONLINE') {
      setProgramOptions([
        'Basic 21 Days',
        'Advanced 21 Days',
        '30 Days Weight Loss'
      ]);
    } else if (newCustomer.customerType === 'CLUB') {
      setProgramOptions([
        'Trial Program',
        'Fitness Plan',
        'Advanced Weight Loss',
        'Premium Weight Loss'
      ]);
    }
    // Reset program selection when customer type changes
    setNewCustomer(prev => ({ ...prev, program: '' }));
  }, [newCustomer.customerType]);

  const fetchCustomers = async () => {
    try {
      setSearchLoading(true);
      
      const params = {
        page: currentPage,
        size: pageSize,
        search: searchQuery,
        sortBy: sortField,
        sortDir: sortDirection,
        statusFilter: statusFilter.length > 0 ? statusFilter.join(',') : ''
      };
      
      const response = await axios.get('/api/customers/direct-customers-paged', { params });
      const data = response.data;
      
      setCustomers(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchImmediateChildren = async () => {
    try {
      const response = await axios.get('/api/customers/referral-options');
      setImmediateChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch referral options:', error);
    }
  };

  const fetchDiscountPlans = async () => {
    try {
      const response = await axios.get('/api/products/discount-plans');
      setDiscountPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch discount plans:', error);
    }
  };

  const fetchProductDiscountPlans = async () => {
    try {
      const response = await axios.get('/api/products/product-discount-plans');
      setProductDiscountPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch product discount plans:', error);
    }
  };

  const validateEmail = async (email, excludeUserId = null) => {
    if (!email || !email.includes('@')) {
      setEmailValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    setEmailValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    try {
      const params = { email };
      if (excludeUserId) {
        params.excludeUserId = excludeUserId;
      }
      
      const response = await axios.get('/api/users/check-email', { params });
      
      if (response.data.exists) {
        setEmailValidation({
          isChecking: false,
          isValid: false,
          message: 'This email address is already registered'
        });
      } else {
        setEmailValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to validate email:', error);
      setEmailValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
    }
  };

  const debounceEmailValidation = React.useCallback(
    debounce((email, excludeUserId) => validateEmail(email, excludeUserId), 500),
    []
  );

  const validateUsername = async (username, excludeUserId = null) => {
    if (!username || !username.trim()) {
      setUsernameValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    setUsernameValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    try {
      const params = { username: username.trim() };
      if (excludeUserId) {
        params.excludeUserId = excludeUserId;
      }

      const response = await axios.get('/api/users/check-username', { params });

      if (response.data.exists) {
        setUsernameValidation({
          isChecking: false,
          isValid: false,
          message: 'This username is already registered'
        });
      } else {
        setUsernameValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to validate username:', error);
      setUsernameValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
    }
  };

  const debounceUsernameValidation = React.useCallback(
    debounce((username, excludeUserId) => validateUsername(username, excludeUserId), 500),
    []
  );

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (!emailValidation.isValid) {
      setFormError('Please fix the email validation error before submitting.');
      return;
    }

    if (!usernameValidation.isValid) {
      setFormError('Please fix the username validation error before submitting.');
      return;
    }
    
    setFormLoading(true);
    setFormError('');

    try {
      await axios.post('/api/customers/add-customer', newCustomer);
      
      // Track successful customer addition
      trackFormSubmission('add_customer_form', true);
      trackEvent('customer_added', {
        event_category: 'Customer Management',
        event_label: 'New Customer Added',
        customer_type: newCustomer.customerType,
        program: newCustomer.program
      });
      
      setShowAddForm(false);
      setShowConfirmModal(true);
      setNewCustomer({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        gender: 'MALE',
        phone: '',
        phoneCountryId: null,
        address: '',
        pcId: '',
        customerType: 'ONLINE',
        referredById: '',
        referredBySearch: '',
        showReferralDropdown: false,
        filteredReferralOptions: [],
        checkupDay: 'MONDAY',
        program: '',
        programStartDate: '',
        programEndDate: '',
        memberRoleCode: 'CUSTOMER',
        programDiscountSlabId: '',
        productDiscountSlabId: '',
        dateOfBirth: '',
        age: '',
        heightCm: '',
        idealWeightKg: '',
        initialWeightKg: '',
        discomfortsMedications: ''
      });
      setCurrentPage(0);
      fetchCustomers();
      setEmailValidation({ isChecking: false, isValid: true, message: '' });
      setUsernameValidation({ isChecking: false, isValid: true, message: '' });
    } catch (error) {
      console.error('Failed to add customer:', error);
      trackFormSubmission('add_customer_form', false);
      setFormError(error.response?.data?.message || 'Failed to add customer. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const handleSearch = (query) => {
    if (query && query.length >= 2) {
      trackSearch(query, 'customers');
    }
    setSearchQuery(query);
    setCurrentPage(0);
  };

  const handleStatusFilterChange = (status) => {
    const newStatusFilter = statusFilter.includes(status)
      ? statusFilter.filter(s => s !== status)
      : [...statusFilter, status];
    
    setStatusFilter(newStatusFilter);
    setCurrentPage(0);
  };

  const clearAllFilters = () => {
    setStatusFilter([]);
    setSearchQuery('');
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'ARCHIVED': return 'var(--error)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const handleViewCustomer = (customer) => {
    trackEvent('view_customer_details', {
      event_category: 'Customer Management',
      event_label: customer.fullName,
      customer_id: customer.id
    });
    navigate(`/customers/${customer.id}`);
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const params = {
        search: searchQuery,
        statusFilter: statusFilter.length > 0 ? statusFilter.join(',') : ''
      };
      
      const response = await axios.get('/api/customers/export-csv', {
        params,
        responseType: 'blob',
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `customers_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      trackEvent('customers_exported', {
        event_category: 'Customer Management',
        event_label: 'CSV Export',
        total_customers: totalElements
      });
    } catch (error) {
      console.error('Failed to export customers:', error);
      setError('Failed to export customers. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>My Customers</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Direct customers under your network
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap' }}>
          <div ref={statusFilterRef} style={{ position: 'relative' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowStatusFilter(!showStatusFilter)}
              style={{ 
                background: statusFilter.length > 0 ? 'var(--primary-blue)' : 'var(--light-blue)',
                color: statusFilter.length > 0 ? 'white' : 'var(--dark-blue)',
                border: 'none',
                position: 'relative'
              }}
            >
              🔍 Filter Status
              {statusFilter.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: 'var(--error)',
                  color: 'white',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {statusFilter.length}
                </span>
              )}
            </button>
            
            {showStatusFilter && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                background: 'var(--white)',
                border: '2px solid var(--primary-blue)',
                borderRadius: '8px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                minWidth: '200px',
                marginTop: '5px'
              }}>
                <div style={{
                  padding: '12px',
                  borderBottom: '1px solid var(--gray-medium)',
                  background: 'var(--pale-blue)',
                  fontWeight: 'bold',
                  color: 'var(--dark-blue)',
                  fontSize: '14px'
                }}>
                  Filter by Status
                </div>
                
                {['ACTIVE', 'INACTIVE', 'ARCHIVED'].map(status => (
                  <label 
                    key={status}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      borderBottom: '1px solid var(--gray-light)'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={statusFilter.includes(status)}
                      onChange={() => handleStatusFilterChange(status)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{
                      color: status === 'ACTIVE' ? 'var(--success)' : 
                             status === 'INACTIVE' ? 'var(--warning)' : 'var(--error)'
                    }}>
                      {status}
                    </span>
                  </label>
                ))}
                
                <div style={{ padding: '10px 12px', borderTop: '1px solid var(--gray-medium)' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={clearAllFilters}
                    style={{ 
                      fontSize: '12px', 
                      padding: '6px 12px',
                      width: '100%',
                      background: 'var(--error)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button 
            className="btn btn-secondary"
            onClick={handleExportCSV}
            disabled={exportLoading || totalElements === 0}
            style={{ 
              background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
              color: 'white',
              border: 'none'
            }}
          >
            {exportLoading ? '⏳ Exporting...' : '📊 Export CSV'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <span className="btn-icon">➕</span>
            <span className="btn-text">Add Customer</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Direct Customers ({totalElements} total)
          </h3>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ margin: 0, paddingRight: searchLoading ? '40px' : '35px' }}
            />
            {searchLoading && (
              <div style={{
                position: 'absolute',
                right: '35px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--primary-blue)'
              }}>
                ⏳
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => handleSearch('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Pagination Info */}
        {totalElements > 0 && (
          <div style={{ 
            marginBottom: '15px', 
            fontSize: '14px', 
            color: 'var(--gray-dark)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} customers
              {searchQuery && ` (filtered by "${searchQuery}")`}
              {statusFilter.length > 0 && ` (status: ${statusFilter.join(', ')})`}
            </span>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        )}

        {customers.length === 0 ? (
          searchQuery || statusFilter.length > 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No customers found matching the current filters.
              {(searchQuery || statusFilter.length > 0) && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={clearAllFilters}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No direct customers found. Add customers through the "Manage Associates" section.
            </div>
          )
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('fullName')}
                  >
                    Customer Name {getSortIcon('fullName')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('phone')}
                  >
                    Phone {getSortIcon('phone')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('customerType')}
                  >
                    Type {getSortIcon('customerType')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('totalWeightLoss')}
                  >
                    Weight Loss {getSortIcon('totalWeightLoss')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('referralCount')}
                  >
                    Referrals {getSortIcon('referralCount')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('totalAmountDue')}
                  >
                    Amount Due {getSortIcon('totalAmountDue')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('programEndDate')}
                  >
                    Program End {getSortIcon('programEndDate')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('status')}
                  >
                    Status {getSortIcon('status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <tr 
                    key={customer.id}
                    onClick={() => handleViewCustomer(customer)}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = 'var(--pale-blue)'}
                    onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)'}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {customer.fullName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.phone || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: customer.customerType === 'ONLINE' ? 'var(--primary-blue)' : 'var(--success)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {customer.customerType || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.totalWeightLoss !== null && customer.totalWeightLoss !== undefined ? (
                        <span style={{
                          color: customer.totalWeightLoss >= 0 ? 'var(--success)' : 'var(--error)',
                          fontWeight: 'bold'
                        }}>
                          {customer.totalWeightLoss >= 0 ? '-' : '+'}{Math.abs(customer.totalWeightLoss)} kg
                        </span>
                      ) : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: customer.referralCount > 0 ? 'var(--success)' : 'var(--gray-medium)',
                        color: customer.referralCount > 0 ? 'white' : 'var(--gray-dark)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {customer.referralCount || 0}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.totalAmountDue > 0 ? (
                        <span style={{
                          color: 'var(--error)',
                          fontWeight: 'bold'
                        }}>
                          ₹{customer.totalAmountDue}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--success)' }}>₹0</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.programEndDate ? formatDateForDisplay(customer.programEndDate) : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getStatusColor(customer.status),
                        fontWeight: '500'
                      }}>
                        {customer.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              First
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '5px' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 3) {
                  pageNum = i;
                } else if (currentPage > totalPages - 4) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`btn ${currentPage === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    style={{ 
                      fontSize: '12px', 
                      padding: '6px 10px',
                      minWidth: '35px'
                    }}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>
            
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Next
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Last
            </button>
          </div>
        )}
      </div>


      {/* Add Customer Form Modal */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              Add New Customer
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="form-input"
                    maxLength="50"
                    value={newCustomer.firstName}
                    onChange={(e) => setNewCustomer({...newCustomer, firstName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="form-input"
                    maxLength="50"
                    value={newCustomer.lastName}
                    onChange={(e) => setNewCustomer({...newCustomer, lastName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="gender" className="form-label">
                  Gender *
                </label>
                <select
                  id="gender"
                  className="form-input"
                  value={newCustomer.gender}
                  onChange={(e) => {
                    const gender = e.target.value;
                    setNewCustomer({...newCustomer, gender: gender});
                    
                    // Auto-calculate ideal weight when gender changes
                    if (newCustomer.heightCm && gender) {
                      const idealWeight = calculateIdealWeight(newCustomer.heightCm, gender);
                      if (idealWeight) {
                        setNewCustomer(prev => ({...prev, idealWeightKg: idealWeight.toString()}));
                      }
                    }
                  }}
                  required
                  disabled={formLoading}
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="dateOfBirth" className="form-label">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    className="form-input"
                    value={newCustomer.dateOfBirth}
                      onChange={(e) => {
                        const dob = e.target.value;
                        setNewCustomer({...newCustomer, dateOfBirth: dob});
                      
                        // Auto-calculate age if DOB is provided
                        if (dob) {
                          const age = calculateAgeFromDateString(dob);
                          setNewCustomer(prev => ({...prev, age: age ? age.toString() : ''}));
                        }
                      }}
                      disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="age" className="form-label">
                    Age (years)
                  </label>
                  <input
                    type="number"
                    id="age"
                    className="form-input"
                    value={newCustomer.age}
                    onChange={(e) => setNewCustomer({...newCustomer, age: e.target.value})}
                    disabled={formLoading}
                    placeholder="Auto-filled from DOB"
                    min="1"
                    max="120"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="heightCm" className="form-label">
                    Height (cm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="heightCm"
                    className="form-input"
                    value={newCustomer.heightCm}
                    onChange={(e) => {
                      const height = e.target.value;
                      setNewCustomer({...newCustomer, heightCm: height});
                      
                      // Auto-calculate ideal weight when height changes
                      if (height && newCustomer.gender) {
                        const idealWeight = calculateIdealWeight(height, newCustomer.gender);
                        if (idealWeight) {
                          setNewCustomer(prev => ({...prev, idealWeightKg: idealWeight.toString()}));
                        }
                      }
                    }}
                    disabled={formLoading}
                    placeholder="e.g., 175.5"
                    min="50"
                    max="250"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="initialWeightKg" className="form-label">
                    Initial Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="initialWeightKg"
                    className="form-input"
                    value={newCustomer.initialWeightKg}
                    onChange={(e) => setNewCustomer({...newCustomer, initialWeightKg: e.target.value})}
                    disabled={formLoading}
                    placeholder="e.g., 85.5"
                    min="20"
                    max="300"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="idealWeightKg" className="form-label">
                    Ideal Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="idealWeightKg"
                    className="form-input"
                    value={newCustomer.idealWeightKg}
                    onChange={(e) => setNewCustomer({...newCustomer, idealWeightKg: e.target.value})}
                    disabled={formLoading}
                    placeholder="e.g., 70.0"
                    min="20"
                    max="300"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number *
                  </label>
                  <CountryPhoneInput
                    phoneValue={newCustomer.phone}
                    countryValue={newCustomer.phoneCountryId}
                    onPhoneChange={(phone) => setNewCustomer({...newCustomer, phone})}
                    onCountryChange={(countryId) => setNewCustomer({...newCustomer, phoneCountryId: countryId})}
                    disabled={formLoading}
                    required={true}
                    placeholder="Enter phone number"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="email"
                        id="email"
                        className={`form-input ${!emailValidation.isValid ? 'error' : ''}`}
                        value={newCustomer.email}
                        onChange={(e) => {
                          const email = e.target.value;
                          setNewCustomer({...newCustomer, email: email});
                          debounceEmailValidation(email);
                        }}
                        required
                        disabled={formLoading}
                        style={{
                          paddingRight: emailValidation.isChecking ? '40px' : '10px',
                          borderColor: !emailValidation.isValid ? 'var(--error)' : undefined
                        }}
                      />
                      {emailValidation.isChecking && (
                        <div style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--primary-blue)'
                        }}>
                          ⏳
                        </div>
                      )}
                    </div>
                    {!emailValidation.isValid && emailValidation.message && (
                      <div style={{
                        color: 'var(--error)',
                        fontSize: '12px',
                        marginTop: '5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        ❌ {emailValidation.message}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="username" className="form-label">
                      Username *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        id="username"
                        className={`form-input ${!usernameValidation.isValid ? 'error' : ''}`}
                        value={newCustomer.username}
                        onChange={(e) => {
                          const username = e.target.value;
                          setNewCustomer({...newCustomer, username});
                          debounceUsernameValidation(username);
                        }}
                        required
                        disabled={formLoading}
                        placeholder="Enter username"
                        style={{
                          paddingRight: usernameValidation.isChecking ? '40px' : '10px',
                          borderColor: !usernameValidation.isValid ? 'var(--error)' : undefined
                        }}
                      />
                      {usernameValidation.isChecking && (
                        <div style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--primary-blue)'
                        }}>
                          ⏳
                        </div>
                      )}
                    </div>
                    {!usernameValidation.isValid && usernameValidation.message && (
                      <div style={{
                        color: 'var(--error)',
                        fontSize: '12px',
                        marginTop: '5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}>
                        ❌ {usernameValidation.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address" className="form-label">
                  Address
                </label>
                <textarea
                  id="address"
                  className="form-input"
                  rows="3"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  disabled={formLoading}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="pcId" className="form-label">
                    PC ID/Associate ID
                  </label>
                  <input
                    type="text"
                    id="pcId"
                    className="form-input"
                    value={newCustomer.pcId}
                    onChange={(e) => setNewCustomer({...newCustomer, pcId: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerType" className="form-label">
                    Customer Type *
                  </label>
                  <select
                    id="customerType"
                    className="form-input"
                    value={newCustomer.customerType}
                    onChange={(e) => setNewCustomer({...newCustomer, customerType: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="ONLINE">Online</option>
                    <option value="CLUB">Club</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="referredBy" className="form-label">
                    Referred By *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="referredBy"
                      className="form-input"
                      placeholder="Search and select referrer..."
                      value={newCustomer.referredBySearch || ''}
                      onChange={(e) => {
                        const searchValue = e.target.value;
                        setNewCustomer({...newCustomer, referredBySearch: searchValue});
                        
                        // Filter options based on search and exclude current customer (not applicable for new customer)
                        const allOptions = [
                          { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                          ...immediateChildren
                        ];
                        const filtered = allOptions.filter(option => 
                          (option.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
                          option.email.toLowerCase().includes(searchValue.toLowerCase()))
                        );
                        setNewCustomer(prev => ({...prev, filteredReferralOptions: filtered}));
                      }}
                      onFocus={() => {
                        const allOptions = [
                          { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                          ...immediateChildren
                        ];
                        // For new customer, no need to exclude anyone since customer doesn't exist yet
                        setNewCustomer(prev => ({...prev, showReferralDropdown: true, filteredReferralOptions: allOptions}));
                      }}
                      required
                      disabled={formLoading}
                    />
                    
                    {newCustomer.showReferralDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'var(--white)',
                        border: '1px solid var(--gray-medium)',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}>
                        {(newCustomer.filteredReferralOptions || []).length === 0 ? (
                          <div style={{ padding: '10px', color: 'var(--gray-dark)' }}>
                            No referral options available
                          </div>
                        ) : (
                          (newCustomer.filteredReferralOptions || []).map(option => (
                            <div
                              key={option.id}
                              style={{
                                padding: '10px',
                                cursor: 'pointer',
                                borderBottom: '1px solid var(--gray-light)'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              onClick={() => {
                                setNewCustomer({
                                  ...newCustomer,
                                  referredById: option.id,
                                  referredBySearch: option.fullName,
                                  showReferralDropdown: false,
                                  filteredReferralOptions: []
                                });
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>{option.fullName}</div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{option.email}</div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="checkupDay" className="form-label">
                    Checkup Day *
                  </label>
                  <select
                    id="checkupDay"
                    className="form-input"
                    value={newCustomer.checkupDay}
                    onChange={(e) => setNewCustomer({...newCustomer, checkupDay: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="MONDAY">Monday</option>
                    <option value="TUESDAY">Tuesday</option>
                    <option value="WEDNESDAY">Wednesday</option>
                    <option value="THURSDAY">Thursday</option>
                    <option value="FRIDAY">Friday</option>
                    <option value="SATURDAY">Saturday</option>
                    <option value="SUNDAY">Sunday</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="program" className="form-label">
                  Program *
                </label>
                <select
                  id="program"
                  className="form-input"
                  value={newCustomer.program}
                  onChange={(e) => setNewCustomer({...newCustomer, program: e.target.value})}
                  required
                  disabled={formLoading}
                >
                  <option value="">Select Program</option>
                  {programOptions.map(program => (
                    <option key={program} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
              </div>


              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="programDiscountSlab" className="form-label">
                    Program Discount Slab
                  </label>
                  <select
                    id="programDiscountSlab"
                    className="form-input"
                    value={newCustomer.programDiscountSlabId}
                    onChange={(e) => setNewCustomer({...newCustomer, programDiscountSlabId: e.target.value})}
                    disabled={formLoading}
                  >
                    {discountPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="productDiscountSlab" className="form-label">
                    Product Discount Slab
                  </label>
                  <select
                    id="productDiscountSlab"
                    className="form-input"
                    value={newCustomer.productDiscountSlabId}
                    onChange={(e) => setNewCustomer({...newCustomer, productDiscountSlabId: e.target.value})}
                    disabled={formLoading}
                  >
                    {productDiscountPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="discomfortsMedications" className="form-label">
                  Discomforts & Medications
                </label>
                <textarea
                  id="discomfortsMedications"
                  className="form-input"
                  rows="4"
                  value={newCustomer.discomfortsMedications}
                  onChange={(e) => setNewCustomer({...newCustomer, discomfortsMedications: e.target.value})}
                  disabled={formLoading}
                  placeholder="Enter any discomforts, medical conditions, or medications..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  value={newCustomer.password}
                  onChange={(e) => setNewCustomer({...newCustomer, password: e.target.value})}
                  required
                  disabled={formLoading}
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormError('');
                    setNewCustomer({
                      firstName: '',
                      lastName: '',
                      email: '',
                      username: '',
                      password: '',
                      gender: 'MALE',
                      phone: '',
                      phoneCountryId: null,
                      address: '',
                      pcId: '',
                      customerType: 'ONLINE',
                      referredById: '',
                      referredBySearch: '',
                      showReferralDropdown: false,
                      filteredReferralOptions: [],
                      checkupDay: 'MONDAY',
                      program: '',
                      memberRoleCode: 'CUSTOMER',
                      programDiscountSlabId: '',
                      productDiscountSlabId: '',
                      dateOfBirth: '',
                      age: '',
                      heightCm: '',
                      idealWeightKg: '',
                      initialWeightKg: '',
                      discomfortsMedications: ''
                    });
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading || !emailValidation.isValid || !usernameValidation.isValid}
                >
                  {formLoading ? 'Adding...' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              Customer Added Successfully!
            </h3>
            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              The new customer has been added to your network.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowConfirmModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
